import {
  getGlobalNotifier,
  type UnsavedChangesDialogEntry,
  useBackendConnection,
  useOptionalFrontendProvision,
  useTranslation,
} from "@atoms/atom-client";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import {
  type BlockerFunction,
  useBlocker,
  useLocation,
} from "react-router-dom";
import {
  isMagicFormRevisionConflictError,
  isMagicFormValidationError,
} from "../../lib/magicFormErrors";
import {
  createUnsavedChangesStore,
  defaultIsSameRoute,
  type UnsavedChangesEntry,
  type UnsavedChangesIsSameRoute,
  type UnsavedChangesStore,
} from "../../lib/unsavedChangesStore";

/** What the user decided about unsaved changes. */
export type UnsavedChangesDecision = "saved" | "discarded" | "cancelled";

/** `"clean"`: there was nothing to ask about, so no dialog opened. */
export type UnsavedChangesLeaveResult = UnsavedChangesDecision | "clean";

type OnlyEntries = (entry: UnsavedChangesEntry) => boolean;

/**
 * How long a rejected save may take to open its form's conflict modal: the
 * form compares the reloaded value over a few renders after `submit` threw.
 */
const CONFLICT_SETTLE_MS = 500;

interface UnsavedChangesHost {
  /**
   * Asks about the dirty entries of `store`, one question at a time. The same
   * `request` gets the open question's answer (StrictMode runs an effect
   * twice), another one `"cancelled"`: an answer covers only what it listed.
   */
  ask: (
    store: UnsavedChangesStore,
    only?: OnlyEntries,
    request?: unknown,
  ) => Promise<UnsavedChangesLeaveResult>;
  /** See {@link UnsavedChangesRegistryProvider}: `beforeunload` stays out. */
  setLeaving: (leaving: boolean) => void;
}

const UnsavedChangesHostContext = createContext<UnsavedChangesHost | undefined>(
  undefined,
);

const UnsavedChangesStoreContext = createContext<
  UnsavedChangesStore | undefined
>(undefined);

interface OpenQuestion {
  request: unknown;
  store: UnsavedChangesStore;
  only: OnlyEntries;
  promise: Promise<UnsavedChangesDecision>;
  resolve: (decision: UnsavedChangesDecision) => void;
}

const everyEntry: OnlyEntries = () => true;

/**
 * The `request` of every route change: a second blocked navigation joins the
 * open question, and its answer goes to the latest destination.
 */
const ROUTE_REQUEST = Symbol("route");

/** For a defect: the expected failures are handled where they occur. */
const reportUnexpected = (error: unknown) => globalThis.reportError(error);

const noSubscription = () => () => undefined;
const noState = () => undefined;

const toDialogEntry = ({
  id,
  displayLabel,
  validationErrors,
}: UnsavedChangesEntry): UnsavedChangesDialogEntry => ({
  id,
  label: displayLabel,
  validationErrors,
});

function createDeferred<Value>() {
  let resolve!: (value: Value) => void;
  const promise = new Promise<Value>((done) => {
    resolve = done;
  });
  return { promise, resolve };
}

let warnedAboutMissingDialog = false;

function warnAboutMissingDialog(): void {
  if (warnedAboutMissingDialog || process.env.NODE_ENV !== "development") {
    return;
  }
  warnedAboutMissingDialog = true;
  console.warn(
    "No `unsavedChangesDialog` in the frontend provision: the unsaved-changes guard falls back to window.confirm. Regenerate the frontend defaults.",
  );
}

/** Keeps the browser's prompt on the page while the store is dirty. */
function useBeforeUnloadPrompt(
  store: UnsavedChangesStore,
  leaving: { current: boolean },
): void {
  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (leaving.current) return;
      store.flushPendingWrites();
      if (store.getDirtyMembers({ live: true }).length === 0) return;
      // Alone enough since Chromium 119; older ones also needed `returnValue`.
      event.preventDefault();
    };
    // Only while dirty, or about to be: the listener keeps a page out of the
    // back/forward cache.
    let attached = false;
    const sync = () => {
      const needed = store.getState().isDirty || store.hasPendingWrites();
      if (needed === attached) return;
      attached = needed;
      if (needed) {
        globalThis.addEventListener("beforeunload", onBeforeUnload);
      } else {
        globalThis.removeEventListener("beforeunload", onBeforeUnload);
      }
    };
    sync();
    const unsubscribe = store.subscribe(sync);
    const unsubscribeFromEntries = store.subscribeToEntries(sync);
    return () => {
      unsubscribe();
      unsubscribeFromEntries();
      globalThis.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [store, leaving]);
}

/**
 * Holds the guard: the registry of editors with unsaved changes, the
 * browser's `beforeunload` prompt and the dialog. Needs no router, so a
 * component test can mount it; `AtomsRouter` mounts it with
 * {@link UnsavedChangesRouteBlocker}. Without it, editors do not register and
 * nothing asks.
 *
 * After the user decided to leave, `beforeunload` stays out until the
 * location settled: the new page's stale-bundle reload runs in a layout
 * effect, before the left forms unregister in their passive cleanup, and must
 * not ask a second time.
 */
export function UnsavedChangesRegistryProvider({
  children,
}: Readonly<{ children: ReactNode }>) {
  const { t } = useTranslation("framework");
  const { isConnected } = useBackendConnection();
  const Dialog = useOptionalFrontendProvision()?.unsavedChangesDialog;
  const [rootStore] = useState(() => createUnsavedChangesStore());
  const [question, setQuestion] = useState<OpenQuestion>();
  const [saving, setSaving] = useState(false);
  const openQuestion = useRef<OpenQuestion | undefined>(undefined);
  const leaving = useRef(false);
  useBeforeUnloadPrompt(rootStore, leaving);

  // The dialog follows the store while it is open: what it lists is what Save
  // and Discard act on.
  useSyncExternalStore(
    question?.store.subscribe ?? noSubscription,
    question?.store.getState ?? noState,
  );
  const listed = question?.store
    .getDirtyMembers({ live: true })
    .filter(question.only);

  const texts = useRef({
    saveFailed: "",
    saveReloaded: "",
    confirm: "",
  });
  texts.current = {
    saveFailed: t(
      "magicForm.unsavedChanges.saveFailed",
      "Speichern fehlgeschlagen. Die Änderungen sind noch nicht gespeichert.",
    ),
    saveReloaded: t(
      "magicForm.unsavedChanges.saveReloaded",
      "Der Eintrag wurde inzwischen geändert und neu geladen. Bitte erneut speichern.",
    ),
    confirm: t(
      "magicForm.unsavedChanges.confirm",
      "Es gibt ungespeicherte Änderungen. Verwerfen und fortfahren?",
    ),
  };

  const ask = useCallback<UnsavedChangesHost["ask"]>(
    async (store, only = everyEntry, request = store) => {
      const open = openQuestion.current;
      if (open) {
        if (open.request !== request) return "cancelled";
        // A new render: the same request may list other entries now.
        setQuestion({ ...open });
        return open.promise;
      }
      store.flushPendingWrites();
      const entries = store.getDirtyMembers({ live: true }).filter(only);
      if (entries.length === 0) {
        return "clean";
      }
      if (!Dialog) {
        warnAboutMissingDialog();
        if (!globalThis.confirm(texts.current.confirm)) {
          return "cancelled";
        }
        try {
          await store.resetAll(only);
          return "discarded";
        } catch (error) {
          reportFailedDecision(error, store, entries, texts.current);
          return "cancelled";
        }
      }
      const { promise, resolve } = createDeferred<UnsavedChangesDecision>();
      const next = { request, store, only, promise, resolve };
      openQuestion.current = next;
      setQuestion(next);
      return promise;
    },
    [Dialog],
  );

  const finish = useCallback((decision: UnsavedChangesDecision) => {
    openQuestion.current?.resolve(decision);
    openQuestion.current = undefined;
    setQuestion(undefined);
    setSaving(false);
  }, []);

  const decide = useCallback(
    async (decision: "saved" | "discarded") => {
      const current = openQuestion.current;
      if (!current) return;
      const { store, only } = current;
      const entries = store.getDirtyMembers({ live: true }).filter(only);
      setSaving(true);
      try {
        if (decision === "saved") {
          await store.saveAll(only);
        } else {
          await store.resetAll(only);
        }
        // A custom save can end without saving (its own question, cancelled).
        // Only `isDirtyNow` knows: `isDirty` predates the editor's next render.
        const unsaved = store
          .getDirtyMembers({ live: true })
          .filter(only)
          .some((entry) => entry.isDirtyNow?.());
        finish(decision === "saved" && unsaved ? "cancelled" : decision);
      } catch (error) {
        // The form shows what happened; the user stays on the page.
        finish("cancelled");
        reportFailedDecision(error, store, entries, texts.current);
      }
    },
    [finish],
  );

  const host = useMemo<UnsavedChangesHost>(
    () => ({
      ask,
      setLeaving: (next) => {
        leaving.current = next;
      },
    }),
    [ask],
  );

  return (
    <UnsavedChangesHostContext.Provider value={host}>
      <UnsavedChangesStoreContext.Provider value={rootStore}>
        {children}
        {Dialog && (
          <Dialog
            open={question !== undefined}
            entries={listed?.map(toDialogEntry) ?? []}
            canSave={
              isConnected &&
              (listed?.every(
                (entry) =>
                  entry.canSave &&
                  !entry.loading &&
                  !entry.validationErrors?.length,
              ) ??
                false)
            }
            saving={saving}
            onSave={() => {
              decide("saved").catch(reportUnexpected);
            }}
            onDiscard={() => {
              decide("discarded").catch(reportUnexpected);
            }}
            onCancel={() => finish("cancelled")}
          />
        )}
      </UnsavedChangesStoreContext.Provider>
    </UnsavedChangesHostContext.Provider>
  );
}

/** Tells the user about a Save or Discard that did not go through. */
function reportFailedDecision(
  error: unknown,
  store: UnsavedChangesStore,
  entries: readonly UnsavedChangesEntry[],
  texts: Readonly<{ saveFailed: string; saveReloaded: string }>,
): void {
  if (isMagicFormValidationError(error)) {
    // The save buttons of the form list the messages.
    return;
  }
  if (!isMagicFormRevisionConflictError(error)) {
    getGlobalNotifier().notify("error", texts.saveFailed);
    return;
  }
  // A reload that overlaps the user's changes opens the form's conflict
  // modal. One that merged cleanly opens nothing, and the save did not run.
  const ids = new Set(entries.map((entry) => entry.id));
  const conflictShown = () =>
    store
      .getDirtyMembers()
      .some((entry) => ids.has(entry.id) && entry.showsConflict);
  if (conflictShown()) return;
  const timer = setTimeout(() => {
    unsubscribe();
    getGlobalNotifier().notify("warning", texts.saveReloaded);
  }, CONFLICT_SETTLE_MS);
  const unsubscribe = store.subscribeToEntries(() => {
    if (conflictShown()) {
      clearTimeout(timer);
      unsubscribe();
    }
  });
}

/**
 * Asks before a route change leaves an editor with unsaved changes. Render it
 * inside the data router and an {@link UnsavedChangesRegistryProvider}.
 */
export function UnsavedChangesRouteBlocker() {
  const host = useContext(UnsavedChangesHostContext);
  const store = useContext(UnsavedChangesStoreContext);
  const location = useLocation();

  // Reads the live dirty state: the router calls this inside `navigate`, and
  // after `await submit(); navigate(…)` the form has not rendered yet. It
  // needs no check of a decided leave: the router skips a blocker that is
  // `proceeding`.
  const blocker = useBlocker(
    useCallback<BlockerFunction>(
      ({ currentLocation, nextLocation }) => {
        store?.flushPendingWrites();
        return (
          store
            ?.getDirtyMembers({ live: true })
            .some(
              (entry) => !entry.isSameRoute(currentLocation, nextLocation),
            ) ?? false
        );
      },
      [store],
    ),
  );
  const latestBlocker = useRef(blocker);
  latestBlocker.current = blocker;

  const blockerState = blocker.state;
  const target = blocker.location;
  useEffect(() => {
    if (blockerState !== "blocked" || !target || !store || !host) return;
    let current = true;
    // The latest destination: a second navigation joins the open question.
    const leaves: OnlyEntries = (entry) =>
      !entry.isSameRoute(location, latestBlocker.current.location ?? target);
    host
      .ask(store, leaves, ROUTE_REQUEST)
      .then((result) => {
        const latest = latestBlocker.current;
        if (!current || latest.state !== "blocked") return;
        if (result === "cancelled") {
          latest.reset();
          return;
        }
        // An editor that stays on screen keeps its prompt.
        host.setLeaving(store.getDirtyMembers({ live: true }).every(leaves));
        latest.proceed();
      })
      .catch(reportUnexpected);
    return () => {
      current = false;
    };
  }, [blockerState, target, location, store, host]);

  // The router sets a proceeding blocker back in the same update that commits
  // the new location, or when the navigation fails.
  useEffect(() => {
    if (blockerState === "unblocked") host?.setLeaving(false);
  }, [blockerState, host]);

  return null;
}

export interface UnsavedChangesRegistration {
  /** Whether the editor has unsaved changes, as of this render. */
  isDirty: boolean;
  /**
   * The same at call time. Give it when a save can be followed by a
   * navigation before the editor's next render.
   */
  isDirtyNow?: () => boolean;
  /** The editor's name in the dialog. */
  label?: ReactNode;
  /** Saves the editor; without it the dialog offers no Save. */
  save?: () => Promise<void>;
  /** Drops the changes. */
  discard?: () => void | Promise<void>;
  /** Default: only a change of the pathname leaves the editor. */
  isSameRoute?: UnsavedChangesIsSameRoute;
  /** What keeps the editor from saving; the dialog lists it. */
  validationErrors?: readonly string[];
  /** Save order, ascending. */
  order?: number;
  loading?: boolean;
  /** Default `true`. */
  enabled?: boolean;
  /** Whether the editor shows its own conflict dialog. */
  showsConflict?: boolean;
}

/**
 * Registers an editor with the guard, so a route change, a closed tab and
 * `confirmLeave` ask about it. Magic forms do it themselves; this is for
 * hand-built editors. Does nothing outside the guard's provider.
 */
export function useUnsavedChanges({
  isDirty,
  isDirtyNow,
  label,
  save,
  discard,
  isSameRoute = defaultIsSameRoute,
  validationErrors,
  order = 0,
  loading = false,
  enabled = true,
  showsConflict,
}: Readonly<UnsavedChangesRegistration>): void {
  const store = useContext(UnsavedChangesStoreContext);
  const id = useId();

  // No dependency array: as in a group, upsert on every render so the store
  // has fresh state and closures.
  useEffect(() => {
    if (!store) return;
    if (!enabled) {
      store.unregister(id);
      return;
    }
    store.register({
      id,
      order,
      displayLabel: label,
      canSave: save !== undefined,
      isSameRoute,
      isDirty,
      isDirtyNow,
      loading,
      validationErrors,
      showsConflict,
      submit: save ?? (async () => undefined),
      reset: async () => {
        await discard?.();
      },
    });
  });

  useEffect(() => {
    if (!store) return;
    return () => store.unregister(id);
  }, [store, id]);
}

/**
 * For a field that passes its text to the form later than the keystroke (a
 * debounce, a commit on blur): the guard must see that text. `markPending`
 * at the keystroke, `clearPending` when the write is through; the guard calls
 * `flush`, which must write synchronously. Does nothing outside the provider.
 */
export function useUnsavedChangesPendingWrite(flush: () => void): {
  markPending: () => void;
  clearPending: () => void;
} {
  const store = useContext(UnsavedChangesStoreContext);
  const id = useId();
  const latestFlush = useRef(flush);
  latestFlush.current = flush;

  useEffect(() => {
    if (!store) return;
    return () => store.setPendingWrite(id, undefined);
  }, [store, id]);

  return useMemo(
    () => ({
      markPending: () =>
        store?.setPendingWrite(id, () => latestFlush.current()),
      clearPending: () => store?.setPendingWrite(id, undefined),
    }),
    [store, id],
  );
}

export interface UnsavedChangesGuard {
  /** True while an editor of the scope has unsaved changes. */
  isDirty: boolean;
  /**
   * Asks the user about the scope's unsaved changes before a modal, a drawer
   * or a hand-built editor goes away. `"clean"`: nothing was dirty, no dialog
   * opened. Close unless it resolves `"cancelled"`.
   */
  confirmLeave: () => Promise<UnsavedChangesLeaveResult>;
  /** For {@link UnsavedChangesScope} around the host's content. */
  scope: UnsavedChangesStore;
}

/**
 * The guard of one modal, drawer or hand-built editor. It covers what renders
 * inside `<UnsavedChangesScope value={guard.scope}>`, so closing a modal does
 * not ask about the forms of the page behind it. Route changes and a closed
 * tab still ask about the scope too.
 */
export function useUnsavedChangesGuard(): UnsavedChangesGuard {
  const host = useContext(UnsavedChangesHostContext);
  const parent = useContext(UnsavedChangesStoreContext);
  const [scope] = useState(() => createUnsavedChangesStore(parent));
  const { isDirty } = useSyncExternalStore(
    scope.subscribe,
    scope.getState,
    scope.getState,
  );
  const confirmLeave = useCallback(
    async () => (host ? host.ask(scope) : "clean"),
    [host, scope],
  );
  return { isDirty, confirmLeave, scope };
}

/** The content of a host of {@link useUnsavedChangesGuard}. */
export function UnsavedChangesScope({
  value,
  children,
}: Readonly<{ value: UnsavedChangesStore; children: ReactNode }>) {
  return (
    <UnsavedChangesStoreContext.Provider value={value}>
      {children}
    </UnsavedChangesStoreContext.Provider>
  );
}
